import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { type AxiosError } from 'axios';
import { ROUTES } from '~/utils/routes';
import {
  CreateClientAdminUserFormFieldValues,
  createClientAdminUserSchema,
} from '../logics/schema';
import { CreateClientAdminUserFormPresentation } from '../presentations';
import { usePostClientAdminUser } from './usePostClientAdminUser';

type PresentationProps = React.ComponentProps<typeof CreateClientAdminUserFormPresentation>;

type IUseCreateClientAdminUserForm = () => {
  state: {
    methods: UseFormReturn<CreateClientAdminUserFormFieldValues>;
    isSubmitDisabled: PresentationProps['isSubmitDisabled'];
  };
  actions: {
    handleSubmit: PresentationProps['onSubmit'];
  };
};

export const useCreateClientAdminUserForm: IUseCreateClientAdminUserForm = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const methods = useForm<CreateClientAdminUserFormFieldValues>({
    defaultValues: {
      fullName: '',
      email: '',
    },
    criteriaMode: 'all',
    mode: 'onChange',
    resolver: zodResolver(createClientAdminUserSchema),
  });

  const threeLetterCode = getThreeLetterCode() || '';
  const { trigger, isMutating } = usePostClientAdminUser(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: '管理者を追加しました。',
        status: 'success',
      });
      navigate(ROUTES.ADMINS.INDEX);
    },
    onError: (error: AxiosError<{ errors?: string[] }>) => {
      if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors && errors.length > 0) {
          showToast({
            message: errors[0],
            status: 'danger',
          });
          return;
        }
      }

      showToast({
        message: '管理者の追加に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleSubmit: PresentationProps['onSubmit'] = methods.handleSubmit(data => {
    trigger({
      client_admin_user: {
        full_name: data.fullName,
        email: data.email,
      },
    });
  });

  return {
    state: {
      methods,
      isSubmitDisabled: !methods.formState.isValid || isMutating,
    },
    actions: {
      handleSubmit,
    },
  };
};
